function removeDiacritics (text = '') {
  try {
    return String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  } catch {
    return String(text)
  }
}

function normalizeText (text = '') {
  return removeDiacritics(String(text).toLowerCase())
}

function escapeRegex (text = '') {
  return String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function maskIgnoredBodyPartExpressions (text = '', { specialityId, bodyPartsData } = {}) {
  const normalizedText = normalizeText(text).replace(/<[^>]*>/g, ' ')
  const expressions = bodyPartsData?.pt?.ignoredExpressionsBySpecialityId?.[specialityId]
  if (!expressions?.length) return normalizedText

  return expressions.reduce((maskedText, expression) => {
    const pattern = normalizeText(expression)
      .trim()
      .split(/\s+/)
      .map(escapeRegex)
      .join('[\\s-]*')

    return maskedText.replace(new RegExp(`\\b${pattern}\\b`, 'gi'), ' ')
  }, normalizedText)
}

/**
 * Build morphological variations of a Portuguese keyword.
 */
function buildPortugueseVariations (keyword) {
  const variations = []

  if (keyword.endsWith('ao')) {
    variations.push(keyword.slice(0, -2) + 'oes')
    variations.push(keyword.slice(0, -2) + 'aes')
  } else if (keyword.endsWith('ado')) {
    const stem = keyword.slice(0, -3)
    variations.push(stem + 'ada', stem + 'ados', stem + 'adas')
  } else if (keyword.endsWith('ido')) {
    const stem = keyword.slice(0, -3)
    variations.push(stem + 'ida', stem + 'idos', stem + 'idas')
  } else if (keyword.endsWith('ico')) {
    const stem = keyword.slice(0, -3)
    variations.push(stem + 'ica', stem + 'icos', stem + 'icas')
  } else if (keyword.endsWith('ica')) {
    const stem = keyword.slice(0, -3)
    variations.push(stem + 'ico', stem + 'icos', stem + 'icas')
  } else if (keyword.endsWith('al')) {
    variations.push(keyword.slice(0, -2) + 'ais')
  }

  if (!variations.length) {
    variations.push(keyword + 's')
    if (!keyword.endsWith('s') && !keyword.endsWith('x') && !keyword.endsWith('z')) {
      variations.push(keyword + 'es')
    }
  }

  return variations
}

function uniqueRanges (ranges = []) {
  const seen = new Set()

  return ranges.filter((range) => {
    const key = `${range.start}:${range.end}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function findPhraseMatches (textNorm, phraseNorm) {
  const matches = []
  let startIndex = 0

  while (startIndex < textNorm.length) {
    const index = textNorm.indexOf(phraseNorm, startIndex)
    if (index === -1) break

    matches.push({
      start: index,
      end: index + phraseNorm.length
    })

    startIndex = index + 1
  }

  return matches
}

function findPatternMatchesInNormalizedText (textNorm = '', patternNorm = '') {
  if (!textNorm || !patternNorm) return []

  if (patternNorm.includes(' ')) {
    return findPhraseMatches(textNorm, patternNorm)
  }

  const forms = Array.from(new Set([patternNorm, ...buildPortugueseVariations(patternNorm)]))
  const matches = []

  for (const form of forms) {
    try {
      const regex = new RegExp(`\\b${escapeRegex(form)}\\b`, 'gi')
      let match = regex.exec(textNorm)

      while (match) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length
        })

        if (regex.lastIndex === match.index) {
          regex.lastIndex += 1
        }

        match = regex.exec(textNorm)
      }
    } catch {
      // Ignore invalid regex edge cases and continue with the next form.
    }
  }

  return uniqueRanges(matches)
}

function normalizeKeywordEntry (entry) {
  const word = typeof entry === 'string'
    ? entry
    : (entry?.word || entry?.label || '')
  const patterns = Array.isArray(entry?.patterns) && entry.patterns.length
    ? entry.patterns.filter(Boolean)
    : (word ? [word] : [])
  const ignorePatterns = Array.isArray(entry?.ignorePatterns)
    ? entry.ignorePatterns.filter(Boolean)
    : []

  return {
    word: word || patterns[0] || '',
    patterns,
    ignorePatterns
  }
}

function entryContainsKeyword (text = '', entry) {
  const textNorm = normalizeText(text)
  const { patterns, ignorePatterns } = normalizeKeywordEntry(entry)

  if (!textNorm || !patterns.length) return false

  const ignoredRanges = uniqueRanges(ignorePatterns.flatMap((pattern) =>
    findPatternMatchesInNormalizedText(textNorm, normalizeText(pattern))
  ))

  return patterns.some((pattern) => {
    const positiveMatches = findPatternMatchesInNormalizedText(textNorm, normalizeText(pattern))

    return positiveMatches.some((match) => !ignoredRanges.some((ignoredRange) =>
      match.start >= ignoredRange.start && match.end <= ignoredRange.end
    ))
  })
}

/**
 * Check if text contains a keyword (accent-insensitive, punctuation-aware,
 * with Portuguese morphological variations).
 */
export function containsKeyword (text, keyword) {
  if (!text || !keyword) return false

  const textNorm = normalizeText(text)
  const kwNorm = normalizeText(keyword)

  return findPatternMatchesInNormalizedText(textNorm, kwNorm).length > 0
}

/**
 * Find all matching keywords in text. Returns array of { word }.
 */
export function findKeywordsInText (text = '', keywords = [], context = {}) {
  const searchableText = maskIgnoredBodyPartExpressions(text, context)
  const found = []
  for (const entry of keywords) {
    const { word } = normalizeKeywordEntry(entry)
    if (!word) continue
    if (entryContainsKeyword(searchableText, entry) && !found.some((f) => f.word === word)) {
      found.push({ word })
    }
  }
  return found
}

/** @deprecated Use findKeywordsInText */
export const findConflictKeywordsInText = findKeywordsInText

export function findStatusConflict ({
  reportText = '',
  status,
  reportKeywords,
  normalStatusValue,
  abnormalStatusValue
} = {}) {
  if (!reportText?.trim() || !reportKeywords) return null

  if (status === normalStatusValue) {
    const keywords = findKeywordsInText(reportText, reportKeywords.abnormalKeywords)
    if (keywords.length) {
      return {
        conflictType: 'normal_status_with_abnormal_text',
        keywords
      }
    }
  }

  if (status === abnormalStatusValue) {
    const keywords = findKeywordsInText(reportText, reportKeywords.normalKeywords)
    if (keywords.length) {
      return {
        conflictType: 'abnormal_status_with_normal_text',
        keywords
      }
    }
  }

  return null
}

export function checkLateralityConflict ({
  specialityId,
  reportText,
  lateralityData,
  findKeywords
} = {}) {
  const result = { hasConflict: false, examSide: '', oppositeSide: '', conflictKeywords: [] }

  if (!specialityId || !reportText?.trim() || !lateralityData) return result

  const finder = findKeywords || findKeywordsInText

  const isLeftExam = lateralityData.left.specialityIds.includes(specialityId)
  const isRightExam = lateralityData.right.specialityIds.includes(specialityId)

  if (!isLeftExam && !isRightExam) return result

  const examSide = isLeftExam ? 'left' : 'right'
  const oppositeSide = isLeftExam ? 'right' : 'left'

  const oppositeKeywords = lateralityData[oppositeSide].keywords
  const conflictMatches = finder(reportText, oppositeKeywords)

  if (!conflictMatches || conflictMatches.length === 0) return result

  const correctKeywords = lateralityData[examSide].keywords
  const correctMatches = finder(reportText, correctKeywords)

  if (correctMatches && correctMatches.length > 0) return result

  result.hasConflict = true
  result.examSide = examSide
  result.oppositeSide = oppositeSide
  result.conflictKeywords = conflictMatches

  return result
}

/**
 * Resolve which gender a specialty is exclusive to.
 * Returns 'F', 'M', or null.
 */
export function getConflictingGenderForSpecialty ({ specialityId, genderExclusiveExams } = {}) {
  if (!genderExclusiveExams || !specialityId) return null

  const matchesSpeciality = (exam) =>
    exam.id === specialityId ||
    (Array.isArray(exam.specialityIds) && exam.specialityIds.includes(specialityId))

  const femaleExams = genderExclusiveExams.femaleExclusiveExams?.specialties || []
  const maleExams = genderExclusiveExams.maleExclusiveExams?.specialties || []

  if (femaleExams.some(matchesSpeciality)) return 'F'
  if (maleExams.some(matchesSpeciality)) return 'M'
  return null
}

/**
 * Detect a gender divergence, either because the exam specialty is exclusive to
 * the opposite gender, or because the report text mentions opposite-gender
 * keywords.
 * Returns { hasConflict, source: 'specialty'|'keyword'|'', examGender, keywords }.
 */
export function findGenderConflict ({ specialityId, patientGender, reportText = '', genderExclusiveExams } = {}) {
  const result = { hasConflict: false, source: '', examGender: '', keywords: [] }

  if (!genderExclusiveExams) return result

  // Specialty-based gender conflict
  if (patientGender && specialityId) {
    const conflictGender = getConflictingGenderForSpecialty({ specialityId, genderExclusiveExams })
    if (conflictGender && conflictGender !== patientGender) {
      return { hasConflict: true, source: 'specialty', examGender: conflictGender, keywords: [] }
    }
  }

  // Keyword-based gender conflict
  if (!reportText?.trim()) return result

  const femaleExams = genderExclusiveExams.femaleExclusiveExams?.specialties || []
  const maleExams = genderExclusiveExams.maleExclusiveExams?.specialties || []

  const keywordsToSearch = []
  let conflictExamGender = ''

  if (!patientGender) {
    for (const exam of [...femaleExams, ...maleExams]) {
      if (exam.keywords?.length) keywordsToSearch.push(...exam.keywords)
    }
  } else {
    const oppositeExams = patientGender === 'F' ? maleExams : femaleExams
    for (const exam of oppositeExams) {
      if (exam.keywords?.length) keywordsToSearch.push(...exam.keywords)
    }
    conflictExamGender = patientGender === 'F' ? 'M' : 'F'
  }

  if (!keywordsToSearch.length) return result

  const matches = findKeywordsInText(reportText, keywordsToSearch)
  if (!matches.length) return result

  return { hasConflict: true, source: 'keyword', examGender: conflictExamGender, keywords: matches }
}

const BODY_PART_LATERALITY_TERMS = new Set([
  'esquerdo', 'esquerda', 'esquerdos', 'esquerdas', 'direito', 'direita', 'direitos', 'direitas'
])
const BODY_PART_AMBIGUOUS_TERMS = new Set([
  'articulacao', 'articulação', 'regiao', 'região', 'tecido', 'musculo', 'músculo', 'nervo',
  'pele', 'ligamento', 'tendao', 'tendão', 'cartilagem', 'veia', 'arteria', 'artéria', 'linfonodo'
])

/**
 * Detect a body-part divergence: the report mentions a body part that does not
 * belong to the exam's expected body part(s).
 * Returns { hasConflict, expectedBases: string[], conflictKeywords: [{ word, bodyPart }] }.
 */
export function findBodyPartConflict ({ examName = '', reportText = '', specialityId, bodyPartsData } = {}) {
  const result = { hasConflict: false, expectedBases: [], conflictKeywords: [] }

  const examNameLower = String(examName).toLowerCase()
  if (!examNameLower || !reportText?.trim()) return result

  const ptBodyParts = bodyPartsData?.pt?.bodyParts
  if (!Array.isArray(ptBodyParts) || ptBodyParts.length === 0) return result

  const normalizedExamName = removeDiacritics(examNameLower).toLowerCase()

  const expectedBases = []
  for (const bp of ptBodyParts) {
    const baseNorm = removeDiacritics(bp.base).toLowerCase()
    const variations = (bp.variations || []).map((v) => removeDiacritics(v).toLowerCase())
    if (normalizedExamName.includes(baseNorm) || variations.some((v) => normalizedExamName.includes(v))) {
      expectedBases.push(bp.base)
    }
  }
  if (expectedBases.length === 0) return result

  const expectedVariations = new Set()
  for (const bp of ptBodyParts) {
    if (expectedBases.includes(bp.base)) {
      for (const v of (bp.variations || [])) {
        expectedVariations.add(removeDiacritics(v).toLowerCase())
      }
    }
  }

  const foundConflicts = []
  const normalizedText = maskIgnoredBodyPartExpressions(reportText, { specialityId, bodyPartsData })

  for (const bp of ptBodyParts) {
    if (expectedBases.includes(bp.base)) continue
    const baseNorm = removeDiacritics(bp.base).toLowerCase()
    if (BODY_PART_AMBIGUOUS_TERMS.has(baseNorm) || BODY_PART_LATERALITY_TERMS.has(baseNorm)) continue

    for (const variation of (bp.variations || [])) {
      const varNorm = removeDiacritics(variation).toLowerCase()
      if (varNorm.length < 3 || expectedVariations.has(varNorm) || BODY_PART_AMBIGUOUS_TERMS.has(varNorm) || BODY_PART_LATERALITY_TERMS.has(varNorm)) continue

      try {
        const regex = new RegExp(`\\b${escapeRegex(varNorm)}\\b`, 'i')
        if (regex.test(normalizedText)) {
          if (!foundConflicts.some((k) => k.word === variation)) {
            foundConflicts.push({ word: variation, bodyPart: bp.base })
          }
          break
        }
      } catch {
        if (normalizedText.includes(varNorm)) {
          foundConflicts.push({ word: variation, bodyPart: bp.base })
          break
        }
      }
    }
  }

  if (!foundConflicts.length) return result

  result.hasConflict = true
  result.expectedBases = expectedBases
  result.conflictKeywords = foundConflicts
  return result
}

/**
 * Collect the conflicting keywords (and specialty name) configured for a
 * specialty. Returns { keywords: string[], name: string }.
 */
export function collectSpecialtyKeywords ({ specialtyConflicts, specialityId } = {}) {
  const conflicts = specialtyConflicts?.pt?.conflicts
  if (!Array.isArray(conflicts)) return { keywords: [], name: '' }

  const toWord = (entry) => (typeof entry === 'string' ? entry : entry?.word)

  // Mirror the component's specialityId index, which is built by iterating the
  // conflict list and overwriting, so the LAST config for an id wins.
  let directConfig = null
  for (const conflict of conflicts) {
    if (conflict && conflict.specialityId === specialityId) directConfig = conflict
  }

  if (directConfig) {
    const keywords = (directConfig.conflictKeywords || []).map(toWord).filter(Boolean)
    return { keywords, name: directConfig.examTypeName || directConfig.name || '' }
  }

  return { keywords: [], name: '' }
}

export function collectValidationWarnings (checks = []) {
  const warnings = []
  for (const check of checks) {
    const result = typeof check === 'function' ? check() : null
    if (result) {
      warnings.push(result)
    }
  }
  return warnings
}
